import { Role } from '@app/utils/protected-routes.utils';

/**
 * Role hierarchy definition with inheritance
 * Each role inherits all permissions from roles in its inheritsFrom array
 */
interface RoleHierarchy {
  role: Role;
  inheritsFrom: Role[];
  level: RoleHierarchyLevels;
  isInternal: boolean;
  isApiKeyRole: boolean;
  displayName: string;
  description: string;
}
/**
 * Hierarchy:
 * SUPER_ADMIN (5) -> OPERATOR (4) -> ADMIN (3) -> INVESTIGATOR (2) / ASSET_PROVIDER (2)
 *                                                         |                    |
 *                                                   REVIEWER (1)           EDR (1)
 */

export enum RoleHierarchyLevels {
  REVIEWER_LEVEL = 1,
  INVESTIGATOR_LEVEL = 2,
  ADMIN_LEVEL = 3,
  OPERATOR_LEVEL = 4,
  SUPER_ADMIN_LEVEL = 5,
}
export const ROLE_HIERARCHY: Record<Role, RoleHierarchy> = {
  [Role.SUPER_ADMIN]: {
    role: Role.SUPER_ADMIN,
    inheritsFrom: [Role.OPERATOR],
    level: RoleHierarchyLevels.SUPER_ADMIN_LEVEL,
    isInternal: true,
    isApiKeyRole: false,
    displayName: 'Super Admin',
    description: 'Full system access; manage companies, tenants, and audit logs',
  },
  [Role.OPERATOR]: {
    role: Role.OPERATOR,
    inheritsFrom: [Role.ADMIN],
    level: RoleHierarchyLevels.OPERATOR_LEVEL,
    isInternal: true,
    isApiKeyRole: false,
    displayName: 'Operator',
    description: 'Operations management; view and manage company/tenant operations',
  },
  [Role.ADMIN]: {
    role: Role.ADMIN,
    inheritsFrom: [Role.INVESTIGATOR, Role.ASSET_PROVIDER],
    level: RoleHierarchyLevels.ADMIN_LEVEL,
    isInternal: false,
    isApiKeyRole: true,
    displayName: 'Admin',
    description: 'Full platform management; users, API keys, configurations, and all investigator/asset features',
  },
  [Role.INVESTIGATOR]: {
    role: Role.INVESTIGATOR,
    inheritsFrom: [Role.REVIEWER],
    level: RoleHierarchyLevels.INVESTIGATOR_LEVEL,
    isInternal: false,
    isApiKeyRole: true,
    displayName: 'Investigator',
    description: 'Create and manage investigations; full investigation access',
  },
  [Role.ASSET_PROVIDER]: {
    role: Role.ASSET_PROVIDER,
    inheritsFrom: [Role.EDR],
    level: RoleHierarchyLevels.INVESTIGATOR_LEVEL,
    isInternal: false,
    isApiKeyRole: true,
    displayName: 'Asset Provider',
    description: 'Manage assets and agents; handle trace extraction jobs',
  },
  [Role.REVIEWER]: {
    role: Role.REVIEWER,
    inheritsFrom: [],
    level: RoleHierarchyLevels.REVIEWER_LEVEL,
    isInternal: false,
    isApiKeyRole: true,
    displayName: 'Reviewer',
    description: 'View-only access to investigations and events',
  },
  [Role.EDR]: {
    role: Role.EDR,
    inheritsFrom: [],
    level: RoleHierarchyLevels.REVIEWER_LEVEL,
    isInternal: false,
    isApiKeyRole: true,
    displayName: 'EDR',
    description: 'Create quick investigations; view tenant information',
  },
};
